// src/containers/Receptions/ReceptionList/hooks/useReceptionActions.tsx
import { useCallback } from "react";
import { useQueryClient } from "@tanstack/react-query";
import SyncIcon from "@mui/icons-material/Sync";
import { ReceptionService, OrderService } from "$services";
import {
  RECEPTION_SYNCHRONIZED_STATUS,
  RECEPTION_SYNCHRONIZED_STATUS_FILTER,
} from "../types";
import { tabs } from "$utils/constants";
import { Reception, Warehouse } from "$types";
import { Button } from "$components";

interface UseReceptionActionsProps {
  wmsConfigs: any[];
  token: string;
  setReceptions: React.Dispatch<React.SetStateAction<any[]>>;
  fetchItems: () => Promise<void>;
  setEditedReception: React.Dispatch<React.SetStateAction<any>>;
  toggleReceptionPopin: (open: boolean) => void;
  setShowOrderModal: React.Dispatch<React.SetStateAction<boolean>>;
  setOrderNewValue: React.Dispatch<React.SetStateAction<any>>;
  setIsSaving: React.Dispatch<React.SetStateAction<boolean>>;
  setIsWarehouseSelected: React.Dispatch<React.SetStateAction<boolean>>;
  editedRowsId: number[];
  setEditedRowsId: React.Dispatch<React.SetStateAction<any[]>>;
  currentTab: number;
  items: any[];
}

const useReceptionActions = ({
  wmsConfigs,
  token,
  setReceptions,
  fetchItems,
  setEditedReception,
  toggleReceptionPopin,
  setShowOrderModal,
  setOrderNewValue,
  setIsSaving,
  setIsWarehouseSelected,
  editedRowsId,
  setEditedRowsId,
  currentTab,
}: UseReceptionActionsProps) => {
  const queryClient = useQueryClient();

  // CRUD operations
  const saveReceptionItem = useCallback(
    async (data: any) => {
      await ReceptionService.createReceptionItem(data);
      queryClient.invalidateQueries({
        queryKey: ["api/order/receptions/consolidated"],
      });
    },
    [queryClient]
  );

  const updateReceptionItem = useCallback(
    async (id: string, data: any) => {
      await ReceptionService.updateReceptionItem(id, data);
      queryClient.invalidateQueries({
        queryKey: ["api/order/receptions/consolidated"],
      });
    },
    [queryClient]
  );

  const removeReceptionItem = useCallback(
    async (id: string) => {
      const result = await ReceptionService.deleteReceptionItem(id);
      queryClient.invalidateQueries({
        queryKey: ["api/order/receptions/consolidated"],
      });
      return result;
    },
    [queryClient]
  );

  // Sync operations
  const onSync = useCallback(
    async (reception: any, push = false) => {
      if (push) {
        await ReceptionService.pushReception(token, [reception]);
      } else {
        await ReceptionService.syncReception(token, [reception]);
      }
    },
    [token]
  );

  const getReceptionAlreadyReceivedQuantity = useCallback(
    async (reception: any) => {
      return ReceptionService.getReceptionQuantity({
        company: reception?.company,
        orderIds: Array.isArray(reception?.orders)
          ? reception.orders.map((o: any) => o?.id || o?._id).filter(Boolean)
          : [],
        productQuantity: reception?.productQuantity || [],
      });
    },
    []
  );

  const getConfigFromReception = useCallback(
    (reception: any) => {
      return wmsConfigs.filter(
        (config: any) =>
          config.config.providers.find(
            (provider: any) => provider.companyId === reception.company
          ) &&
          config.warehouses.includes(reception.warehouse) &&
          new Date(reception.createdAt) > new Date(config.createdAt)
      );
    },
    [wmsConfigs]
  );

  const getReceptionSynchronizedStatus = useCallback(
    (reception: any) => {
      const configsFromReception = getConfigFromReception(reception);

      if (configsFromReception.length <= 0) {
        return RECEPTION_SYNCHRONIZED_STATUS_FILTER.NOT_CONFIGURED;
      }
      if (reception.isSynchronized === false) {
        return RECEPTION_SYNCHRONIZED_STATUS_FILTER.NOT_PUSHED;
      }
      return RECEPTION_SYNCHRONIZED_STATUS_FILTER.PUSHED;
    },
    [getConfigFromReception]
  );

  const getReceptionSynchronizedStatusButton = useCallback(
    (reception: any) => {
      const configsFromReception = getConfigFromReception(reception);

      if (configsFromReception.length <= 0) {
        return null;
      }

      if (reception.isSynchronized === false) {
        return (
          <Button
            variant="outlined"
            onClick={() => {
              onSync(reception, true);
            }}
            style={{
              flex: 1,
              width: "230px",
              display: "flex",
              justifyContent: "flex-start",
            }}
          >
            <SyncIcon style={{ marginRight: "4px" }} />
            {RECEPTION_SYNCHRONIZED_STATUS.NOT_PUSHED}
          </Button>
        );
      }

      return (
        <Button
          variant="outlined"
          onClick={() => {
            onSync(reception);
          }}
        >
          <SyncIcon style={{ marginRight: "4px" }} />
          {RECEPTION_SYNCHRONIZED_STATUS.PUSHED}
        </Button>
      );
    },
    [getConfigFromReception, onSync]
  );

  const updateReception = useCallback(
    (cR: any, field: string, newValue: any) => {
      const receptionId = cR.id;
      const newReception = { ...cR };
      newReception[field] = newValue;
      setReceptions((currentReceptions: any) => {
        const index = currentReceptions.findIndex(
          (r: any) => r.id === receptionId
        );
        const newReceptions = [...currentReceptions];
        newReceptions[index] = newReception;
        return newReceptions;
      });
    },
    [setReceptions]
  );

  const handleClick = useCallback(
    async (elem: any) => {
      setEditedReception(elem);
      toggleReceptionPopin(true);
    },
    [setEditedReception, toggleReceptionPopin]
  );

  const saveOrderItem = useCallback(async (data: any) => {
    await OrderService.createOrderItem(data);
  }, []);

  const onSubmitOrder = useCallback(
    async (data: any) => {
      const order = { ...data };
      delete order?.id;

      await saveOrderItem(order);
      setShowOrderModal(false);
      setOrderNewValue(undefined);
    },
    [saveOrderItem, setOrderNewValue, setShowOrderModal]
  );

  const checkMandatoryFields = useCallback(
    (reception: any) => {
      if (!reception) return false;

      if (currentTab === tabs.PROVIDER_TAB) {
        return (
          !!reception.receptionDate &&
          !!reception.provider &&
          !!reception.company &&
          !!reception.warehouse
        );
      }
      return (
        !!reception.receptionDate &&
        !!reception.warehouseOrigin &&
        !!reception.company &&
        !!reception.warehouse
      );
    },
    [currentTab]
  );

  const handleSaveReception = useCallback(
    async (currentReception: any) => {
      const receptionId = currentReception.id ?? 0;
      if (checkMandatoryFields(currentReception)) {
        setIsSaving(true);
        if (currentReception.id) {
          await updateReceptionItem(receptionId, {
            ...currentReception,
            orders: currentReception.orders?.data || currentReception.orders,
            productQuantity:
              currentReception.productQuantity?.data ||
              currentReception.productQuantity ||
              [],
          });
        } else {
          delete currentReception.id;
          let updatedReception;
          if (currentTab === tabs.TRANSFER_TAB) {
            updatedReception = { ...currentReception, isTransfer: true };
          }
          await saveReceptionItem({
            ...(updatedReception ?? currentReception),
            orders: currentReception.orders?.data || currentReception.orders,
            productQuantity:
              currentReception.productQuantity?.data ||
              currentReception.productQuantity ||
              [],
          });
        }
        await fetchItems();
        setIsWarehouseSelected(false);
        setEditedRowsId(editedRowsId.filter((i: number) => i !== receptionId));
        setIsSaving(false);
      }
    },
    [
      checkMandatoryFields,
      currentTab,
      editedRowsId,
      fetchItems,
      saveReceptionItem,
      setEditedRowsId,
      setIsSaving,
      setIsWarehouseSelected,
      updateReceptionItem,
    ]
  );

  const onSubmitReception = useCallback(
    async (data: any) => {
      // Keep the same create/update logic as the former inline row.
      // `handleSaveReception` already:
      // - validates mandatory fields depending on the tab (provider/transfer)
      // - does POST on create and PUT on edit
      // - refreshes the list
      await handleSaveReception(data);
      toggleReceptionPopin(false);
    },
    [handleSaveReception, toggleReceptionPopin]
  );

  // Return an array of ID, id from entrepot.comapnies or his parentCompany (if entrepot.comapnies is empty)
  const getCompaniesOrParent = (
    entrepots: Warehouse[],
    currentWarehouseId: string
  ) => {
    let result: any[] = [];
    const foundWarehouse: Warehouse = entrepots?.find(
      (e) => e._id === currentWarehouseId
    );
    if (foundWarehouse && foundWarehouse._id === currentWarehouseId) {
      if (foundWarehouse.companies.length > 0) {
        // Extract all ids
        result = foundWarehouse.companies.map((company) => company._id);
      } else {
        result.push(foundWarehouse.parentCompany);
      }
    }
    return result;
  };

  const onOrderSelectValueChanged = useCallback(
    (currentReception: Reception) => async (newValues: any, newOrders: any) => {
      if (newValues?.slice(-1)?.[0]?.inputValue) {
        setShowOrderModal(true);
        setOrderNewValue(newValues?.slice(-1)?.[0]?.inputValue);
        return;
      }
      const receptionId = currentReception.id;
      let newReception = {
        ...currentReception,
        productQuantity: currentReception?.productQuantity
          ? [...currentReception.productQuantity]
          : [],
      };

      if (newOrders.length === 0) {
        setIsWarehouseSelected(false);
        newReception.orders = newReception.orders.filter(
          (order: any) =>
            // Si deux commandes ayant la même référence sont sélectionnées, on ne peut plus les enlever.
            // Il faudrait bloquer la possibilité de sélectionner deux commandes de deux entreprises différentes.
            // C'est aussi valable pour le sélecteur dans le ReceptionForm
            newValues.includes(order.reference) ||
            newValues.find((nV: any) => nV?.inputValue === order.reference)
        );
      } else {
        // Handle both old Strapi structure (orders.data) and new Prisma structure (orders directly)
        const newReceptionOrders = newReception.orders || [];
        const normalizedExistingOrders = newReceptionOrders
          ? newReceptionOrders.map((order: any) => {
              // Handle both Strapi structure (with attributes) and Prisma structure (direct properties)
              if (order.attributes) {
                const { attributes, ...rest } = order;
                return { ...rest, ...attributes };
              }
              return order;
            })
          : [];

        newReception.orders = newReception.orders
          ? [...normalizedExistingOrders, ...newOrders]
          : [...newOrders];
      }

      if (newReception.orders.length >= 1) {
        // Extract company, warehouse, and provider from the first order
        // Orders from Prisma have these as direct properties
        const firstOrder = newReception.orders[0] as any;

        // Handle both Strapi structure (attributes) and Prisma structure (direct properties)
        const orderCompany =
          firstOrder?.company || firstOrder?.attributes?.company;
        const orderWarehouse =
          firstOrder?.warehouse || firstOrder?.attributes?.warehouse;
        const orderProvider =
          firstOrder?.provider || firstOrder?.attributes?.provider;

        // Set company, warehouse, and provider on the reception from the order
        if (orderCompany) {
          newReception.company = orderCompany;
        }
        if (orderWarehouse) {
          newReception.warehouse = orderWarehouse;
        }
        if (orderProvider) {
          newReception.provider = orderProvider;
        }

        newReception = await getReceptionAlreadyReceivedQuantity(newReception);
      } else {
        newReception.company = undefined;
        newReception.warehouse = undefined;
        newReception.provider = undefined;
        newReception.productQuantity = [];
      }

      setReceptions((currentReceptions: any) => {
        const index = currentReceptions.findIndex(
          (r: any) => r.id === receptionId
        );
        const newReceptions = [...currentReceptions];
        newReceptions[index] = newReception;
        return newReceptions;
      });
    },
    [
      getReceptionAlreadyReceivedQuantity,
      setIsWarehouseSelected,
      setOrderNewValue,
      setReceptions,
      setShowOrderModal,
    ]
  );

  return {
    saveReceptionItem,
    updateReceptionItem,
    removeReceptionItem,
    getReceptionAlreadyReceivedQuantity,
    getConfigFromReception,
    getReceptionSynchronizedStatus,
    getReceptionSynchronizedStatusButton,
    onSync,
    updateReception,
    handleClick,
    saveOrderItem,
    onSubmitOrder,
    onSubmitReception,
    checkMandatoryFields,
    handleSaveReception,
    getCompaniesOrParent,
    onOrderSelectValueChanged,
  };
};

export default useReceptionActions;
